import { useEffect, useMemo, useState } from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { setFetcherSettingsBrandTraceData } from "../../../../features/fetcherSettingsSlice";
import { FetcherSettingsEndPointLogic } from "../../../../logic/DataCollection/FetcherSettings/FetcherSettingsEndPointLogic";
import {
  FetcherBrandTraceRow,
  rowId,
  selectBrandTraceBrand,
  selectBrandTraceRows,
  selectCatalogueRows,
} from "../helpers/fetcherCatalogue";
import {
  FETCHER_SETTINGS_BRAND_TRACE_QUERY_ID,
  getFirstWebsiteSharedUid,
} from "../helpers/fetcherSettingsQueryIds";
import BrandTraceConnectionCard from "./BrandTraceConnectionCard";
import BrandTraceSearch, { BrandOption } from "./BrandTraceSearch";
import BrandTraceSkeleton from "./BrandTraceSkeleton";
import BrandTraceSummary from "./BrandTraceSummary";

const SUGGESTED_BRAND_COUNT = 3;

const BrandTrace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const websiteSharedUid = getFirstWebsiteSharedUid(userData);
  const [loading, setLoading] = useState(false);
  const {
    fetcherSettingsMainTablesData,
    isCatalogueLoading,
    fetcherSettingsBrandTraceData,
  } = useSelector((state: AppState) => state.fetcherSettings);

  const [brandId, setBrandId] = useState(() => searchParams.get("brand") ?? "");
  const [expandedConnections, setExpandedConnections] = useState<string[]>([]);

  // mirrors the selected brand back to the URL so the trace can be shared;
  // setSearchParams is rebuilt on every URL change, so it stays out of the deps
  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (brandId) next.set("brand", brandId);
        else next.delete("brand");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    if (!brandId) {
      dispatch(setFetcherSettingsBrandTraceData(null));
      return;
    }
    if (!oauthTokenHeader || !websiteSharedUid) return;
    const loadBrandTrace = async () => {
      try {
        setLoading(true);
        dispatch(setFetcherSettingsBrandTraceData(null));
        await FetcherSettingsEndPointLogic.getFetcherSettingsBrandTraceData({
          oauthTokenHeader,
          queryId: FETCHER_SETTINGS_BRAND_TRACE_QUERY_ID,
          websiteSharedUids: [websiteSharedUid],
          brand_id: brandId,
        });
      } catch (e: any) {
        console.error("Error loading brand trace:", e);
      } finally {
        setLoading(false);
      }
    };
    loadBrandTrace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // the picker's options are the catalogue's brand rows, by name and id
  const brandOptions: BrandOption[] = useMemo(
    () =>
      selectCatalogueRows(fetcherSettingsMainTablesData, "brand")
        .map((row) => ({
          id: rowId(row.item_id),
          name: row.item_name ?? "",
        }))
        .filter((option) => option.id && option.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [fetcherSettingsMainTablesData],
  );

  const selectedBrand = brandOptions.find((option) => option.id === brandId);

  // the query is run for one brand, so there is only ever one brand row
  const tracedBrand = useMemo(
    () => selectBrandTraceBrand(fetcherSettingsBrandTraceData),
    [fetcherSettingsBrandTraceData],
  );

  const connections = useMemo(
    () => selectBrandTraceRows(fetcherSettingsBrandTraceData, "connection"),
    [fetcherSettingsBrandTraceData],
  );

  /**
   * The `section=object` rows grouped by the config they belong to, so each
   * card reads only its own cascade. Grouped once here rather than filtering
   * the whole object list again inside every connection.
   */
  const objectsByConfigId = useMemo(() => {
    const grouped: { [configId: string]: FetcherBrandTraceRow[] } = {};
    selectBrandTraceRows(fetcherSettingsBrandTraceData, "object").forEach(
      (row) => {
        const configId = rowId(row.config_id);
        if (!grouped[configId]) grouped[configId] = [];
        grouped[configId].push(row);
      },
    );
    return grouped;
  }, [fetcherSettingsBrandTraceData]);

  // the picker stays put while the trace loads, so the brand cannot be
  // swapped out from under a request that is already in flight
  const isLoading = loading || isCatalogueLoading;

  return (
    <Box sx={{ p: 2 }}>
      <BrandTraceSearch
        options={brandOptions}
        selectedBrandId={brandId}
        onSelect={setBrandId}
        disabled={isLoading}
      />

      {isLoading ? (
        <BrandTraceSkeleton />
      ) : selectedBrand ? (
        <Box sx={{ mt: 2, border: "1px solid #E7E7EC", borderRadius: "9px" }}>
          {tracedBrand && <BrandTraceSummary brand={tracedBrand} />}

          {connections.map((connection) => {
            const configId = rowId(connection.config_id);
            return (
              <BrandTraceConnectionCard
                key={configId}
                connection={connection}
                brand={tracedBrand}
                objects={objectsByConfigId[configId] ?? []}
                expanded={expandedConnections.includes(configId)}
                onToggle={() =>
                  setExpandedConnections((previous) =>
                    previous.includes(configId)
                      ? previous.filter((id) => id !== configId)
                      : [...previous, configId],
                  )
                }
                onOpenConfig={() => undefined}
                onOpenReport={() => undefined}
              />
            );
          })}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 7 }}>
          <Typography variant="body2" sx={{ color: "#00000099" }}>
            Search a brand to trace exactly what the Fetcher collects for it —
            every config, and the fully resolved object set behind each raw
            table.
          </Typography>
          {brandOptions.length > 0 && (
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Typography sx={{ fontSize: "12.5px", color: "#00000099" }}>
                Try
              </Typography>
              {brandOptions.slice(0, SUGGESTED_BRAND_COUNT).map((option) => (
                <Link
                  key={option.id}
                  component="button"
                  underline="always"
                  color="secondary"
                  sx={{ fontSize: "12.5px" }}
                  onClick={() => setBrandId(option.id)}
                >
                  {option.name}
                </Link>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BrandTrace;
